import axios from 'axios'
import { getUserId } from './auth'

const ANALYTICS_EVENTS = {
  APPLY_NOW_CLICK: 'Apply Now Click',
  COMMUNITY_WEBSITE_VIEW: 'Community Website View',
  DIRECTIONS_VIEW: 'Directions View',
  PHOTO_VIEW: 'Photo View',
  SOCIAL_SHARE: 'Social Media Share',
  VIDEO_VIEW: 'Video View',
}

const GTM_EVENTS = {
  TENANT_MESSAGE: 'TenantMessageLead',
  TENANT_GUEST_CARD: 'TenantGuestCardLead',
  TENANT_APPLY_NOW_CLICK: 'TenantApplyNowClickLead',
  TENANT_WEBSITE_CLICK: 'TenantWebsiteClickLead',
  TENANT_OPENS_LISTING_DETAILS: 'TenantOpensListingDetails',
  TENANT_FAVORITE_LISTING: 'TenantFavoriteListing',
  TENANT_SEARCH: 'TenantSearch',
  TENANT_SAVE_SEARCH: 'TenantSaveSearch',
  TENANT_PHONE_CALL_TEXT_MESSAGE: 'TenantPhoneCallTextMessage',
  TENANT_RECOMMENDATION_CLICK: 'TenantRecommendationClick',
}

/**
 * Tracks an analyitcs event. Will also track the 'User' event for the
 * provided event name if authenticated.
 * @param {String} eventName - The name of the event to track. Should be one of ANALYTICS_EVENTS.
 * @param {Object} properties - Additional data to attach to the event. Values MUST be strings.
 * @returns {Promise}
 */
const track = (eventName, properties) => {
  const url = `${rentler.apiBaseUri}/analytics/events`
  const data = { eventName, properties }
  const requests = [axios.post(url, data)]

  const userId = document
    .querySelector('.main-wrapper')
    .getAttribute('data-listing-user-id')

  if (userId) {
    requests.push(
      axios.post(url, {
        eventName: `User ${eventName}`,
        properties: { UserId: `${userId}` },
      })
    )
  }

  return axios.all(requests)
}

/**
 * Tracks a google tag manager event for when a tenant submits a message.
 * @param {object} props - Key-value object containing any other properties to be send to google tag manager.
 */
const trackGtmTenantMessage = (props) => {
  const main = document.querySelector('.main-wrapper')
  const listingId = +main.getAttribute('data-listing-id') || null
  const listingUserId = +main.getAttribute('data-listing-user-id') || null
  const propertyId = +main.getAttribute('data-listing-property-id') || null
  const userId = getUserId()

  const event = {
    event: GTM_EVENTS.TENANT_MESSAGE,
    listing_id: listingId,
    listing_user_id: listingUserId,
    property_id: propertyId,
    user_id: userId,
    ...props,
  }

  gtmDataLayer.push(event)
}

/**
 * Tracks a google tag manager event for when a tenant submits a guest card.
 * @param {object} props - Key-value object containing any other properties to be send to google tag manager.
 */
const trackGtmTenantGuestCard = (props) => {
  const main = document.querySelector('.main-wrapper')
  const listingId = +main.getAttribute('data-listing-id') || null
  const listingUserId = +main.getAttribute('data-listing-user-id') || null
  const propertyId = +main.getAttribute('data-listing-property-id') || null
  const userId = getUserId()

  const event = {
    event: GTM_EVENTS.TENANT_GUEST_CARD,
    listing_id: listingId,
    listing_user_id: listingUserId,
    property_id: propertyId,
    user_id: userId,
    ...props,
  }

  gtmDataLayer.push(event)
}

/**
 * Tracks a google tag manager event for when a tenant views a custom application website url.
 * @param {object} props - Key-value object containing any other properties to be send to google tag manager.
 */
const trackGtmTenantApplyNowClick = (props) => {
  const main = document.querySelector('.main-wrapper')
  const listingId = +main.getAttribute('data-listing-id') || null
  const listingUserId = +main.getAttribute('data-listing-user-id') || null
  const propertyId = +main.getAttribute('data-listing-property-id') || null
  const userId = getUserId()

  const event = {
    event: GTM_EVENTS.TENANT_APPLY_NOW_CLICK,
    listing_id: listingId,
    listing_user_id: listingUserId,
    property_id: propertyId,
    user_id: userId,
    ...props,
  }

  gtmDataLayer.push(event)
}

/**
 * Tracks a google tag manager event for when a tenant views a custom community website url.
 * @param {object} props - Key-value object containing any other properties to be send to google tag manager.
 */
const trackGtmTenantWebsiteClick = (props) => {
  const main = document.querySelector('.main-wrapper')
  const listingId = +main.getAttribute('data-listing-id') || null
  const listingUserId = +main.getAttribute('data-listing-user-id') || null
  const propertyId = +main.getAttribute('data-listing-property-id') || null
  const userId = getUserId()

  const event = {
    event: GTM_EVENTS.TENANT_WEBSITE_CLICK,
    listing_id: listingId,
    listing_user_id: listingUserId,
    property_id: propertyId,
    user_id: userId,
    ...props,
  }

  gtmDataLayer.push(event)
}

/**
 * Tracks a google tag manager event for when a tenant opens a listing detail.
 */
const trackGtmTenantOpensListingDetail = () => {
  const main = document.querySelector('.main-wrapper')
  const listingId = +main.getAttribute('data-listing-id') || null
  const userId = getUserId()

  if (userId) {
    const event = {
      event: GTM_EVENTS.TENANT_OPENS_LISTING_DETAILS,
      listing_id: listingId,
      user_id: userId,
    }

    gtmDataLayer.push(event)
  }
}

/**
 * Tracks a google tag manager event for when a tenant favorites a listing.
 * @param {object} props - Key-value object containing any other properties to be send to google tag manager.
 */
const trackGtmTenantFavoriteListing = (props) => {
  const listingId = props.listingId || null
  const userId = getUserId()

  if (userId) {
    const event = {
      event: GTM_EVENTS.TENANT_FAVORITE_LISTING,
      listing_id: listingId,
      user_id: userId,
    }
    gtmDataLayer.push(event)
  }
}

/**
 * Tracks a google tag manager event for when a tenant searches.
 * @param {object} props - Key-value object containing any other properties to be send to google tag manager.
 */
const trackGtmTenantSearch = (props) => {
  const search = props.search
  const userId = getUserId()

  const event = {
    event: GTM_EVENTS.TENANT_SEARCH,
    search: search,
    ...(userId && { user_id: userId }),
  }
  gtmDataLayer.push(event)
}

/**
 * Tracks a google tag manager event for when a tenant saves a search.
 * @param {object} props - Key-value object containing any other properties to be send to google tag manager.
 */
const trackGtmTenantSaveSearch = (props) => {
  const search = props.search
  const userId = getUserId()

  if (userId) {
    const event = {
      event: GTM_EVENTS.TENANT_SAVE_SEARCH,
      search: search,
      user_id: userId,
    }
    gtmDataLayer.push(event)
  }
}

/**
 * Tracks a google tag manager event for when a tenant clicks text or call.
 */
const trackGtmTenantPhoneCallTextMessage = () => {
  const main = document.querySelector('.main-wrapper')
  const listingId = +main.getAttribute('data-listing-id') || null
  const userId = getUserId()

  if (userId) {
    const event = {
      event: GTM_EVENTS.TENANT_PHONE_CALL_TEXT_MESSAGE,
      listing_id: listingId,
      user_id: userId,
    }
    gtmDataLayer.push(event)
  }
}

/**
 * Tracks a google tag manager event for when a tenant clicks a recommmended listing.
 */
const trackGtmTenantRecommendationClick = (
  listingId = null,
  recommendedListingId = null,
  props
) => {
  const userId = getUserId()

  const event = {
    event: GTM_EVENTS.TENANT_RECOMMENDATION_CLICK,
    listing_id: listingId,
    recommended_listing_id: recommendedListingId,
    ...(userId && { user_id: userId }),
    ...props,
  }

  gtmDataLayer.push(event)
}

export {
  ANALYTICS_EVENTS,
  track,
  trackGtmTenantMessage,
  trackGtmTenantGuestCard,
  trackGtmTenantApplyNowClick,
  trackGtmTenantWebsiteClick,
  trackGtmTenantOpensListingDetail,
  trackGtmTenantFavoriteListing,
  trackGtmTenantSearch,
  trackGtmTenantSaveSearch,
  trackGtmTenantPhoneCallTextMessage,
  trackGtmTenantRecommendationClick,
}
